import React, { useState } from 'react';
import { Achievement } from '../../types';
import AchievementDetailModal from '../AchievementDetailModal';

interface AchievementsPageProps {
  achievements: Achievement[];
}

const AchievementsPage: React.FC<AchievementsPageProps> = ({ achievements }) => {
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  const unlocked = achievements.filter(a => a.unlocked);
  const locked = achievements.filter(a => !a.unlocked);

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setIsDetailModalOpen(true);
  };

  const categoryColors: Record<string, string> = {
    quest: 'border-system-blue/50 bg-system-blue/10',
    level: 'border-system-purple/50 bg-system-purple/10',
    streak: 'border-system-blue/50 bg-system-blue/10',
    stat: 'border-system-purple/50 bg-system-purple/10',
    special: 'border-system-blue/50 bg-system-blue/10',
  };

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse"></div>
            <span className="font-orbitron text-lg sm:text-2xl tracking-wider text-system-blue uppercase">
              ［ ACHIEVEMENTS ］
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
            Your collection of earned achievements
          </p>
        </div>
      </div>

      {/* Progress Summary */}
      <div 
        className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 mb-4 sm:mb-6 transition-all"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div>
            <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-0.5 sm:mb-1">Progress</div>
            <div className="text-2xl sm:text-3xl font-black font-orbitron text-system-blue">
              {unlocked.length} / {achievements.length}
            </div>
          </div>
          <div className="text-4xl sm:text-6xl animate-float">🏆</div>
        </div>
        <div className="relative w-full bg-system-darker rounded-full h-3 sm:h-4 overflow-hidden border-2 border-system-blue/40">
          <div
            className="h-full bg-gradient-to-r from-system-blue to-system-purple transition-all duration-500"
            style={{ width: `${(unlocked.length / achievements.length) * 100}%` }}
          >
            <div className="h-full w-full animate-pulse opacity-50 bg-white" />
          </div>
        </div>
      </div>

      {/* Unlocked Achievements */}
      {unlocked.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-sm sm:text-base font-bold font-orbitron text-green-400 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
            <span>✓</span>
            <span>UNLOCKED ({unlocked.length})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {unlocked.map((achievement, index) => (
              <div
                key={achievement.id}
                onClick={() => handleAchievementClick(achievement)}
                className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4 animate-fade-in transition-all hover:scale-105 cursor-pointer hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80"
                style={{ 
                  animationDelay: `${index * 0.05}s`,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
                }}
              >
                <div className="text-center">
                  {achievement.image ? (
                    <img 
                      src={achievement.image} 
                      alt={achievement.title}
                      className={`w-12 h-12 sm:w-16 sm:h-16 object-contain mb-2 mx-auto ${
                        index % 3 === 0 ? 'animate-float-3d' : 
                        index % 3 === 1 ? 'animate-float-3d-slow' : 'animate-float-3d-subtle'
                      }`}
                    />
                  ) : (
                    <div className={`text-4xl sm:text-5xl mb-2 ${
                      index % 3 === 0 ? 'animate-float-3d' : 
                      index % 3 === 1 ? 'animate-float-3d-slow' : 'animate-float-3d-subtle'
                    }`}>{achievement.icon}</div>
                  )}
                  <h4 className="font-orbitron font-bold text-white text-xs sm:text-sm mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{achievement.title}</h4>
                  <div className="bg-black/40 backdrop-blur-sm rounded px-2 py-1.5 mb-2">
                    <p className="text-[10px] sm:text-xs text-gray-100 font-rajdhani font-bold line-clamp-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                      {achievement.description}
                    </p>
                  </div>
                  {achievement.unlockedAt && (
                    <div className="text-[10px] sm:text-xs text-green-400 font-rajdhani font-semibold">
                      ✓ Unlocked {new Date(achievement.unlockedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      {locked.length > 0 && (
        <div>
          <h3 className="text-sm sm:text-base font-bold font-orbitron text-gray-500 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
            <img 
              src="/icons/icon-locked.png" 
              alt="Locked"
              className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
              style={{ filter: 'drop-shadow(0 0 4px rgba(107, 114, 128, 0.6))' }}
            />
            <span>LOCKED ({locked.length})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {locked.map((achievement, index) => (
              <div
                key={achievement.id}
                onClick={() => handleAchievementClick(achievement)}
                className="relative bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-700/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 rounded-3xl p-3 sm:p-4 opacity-60 animate-fade-in cursor-pointer hover:scale-105 hover:opacity-80 transition-all"
                style={{ 
                  animationDelay: `${index * 0.05}s`,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -3px 8px rgba(0, 0, 0, 0.6)'
                }}
              >
                <div className="text-center">
                  {achievement.image ? (
                    <img 
                      src={achievement.image} 
                      alt={achievement.title}
                      className={`w-12 h-12 sm:w-16 sm:h-16 object-contain mb-2 mx-auto grayscale ${
                        index % 3 === 0 ? 'animate-float-3d' : 
                        index % 3 === 1 ? 'animate-float-3d-slow' : 'animate-float-3d-subtle'
                      }`}
                    />
                  ) : (
                    <div className={`text-4xl sm:text-5xl mb-2 grayscale ${
                      index % 3 === 0 ? 'animate-float-3d' : 
                      index % 3 === 1 ? 'animate-float-3d-slow' : 'animate-float-3d-subtle'
                    }`}>{achievement.icon}</div>
                  )}
                  <h4 className="font-orbitron font-bold text-gray-400 text-xs sm:text-sm mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{achievement.title}</h4>
                  <div className="bg-black/40 backdrop-blur-sm rounded px-2 py-1.5 mb-2">
                    <p className="text-[10px] sm:text-xs text-gray-300 font-rajdhani font-bold line-clamp-2" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                      {achievement.description}
                    </p>
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-500 font-rajdhani font-semibold">
                    🔒 {achievement.requirement}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Achievement Detail Modal */}
      <AchievementDetailModal
        achievement={selectedAchievement}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedAchievement(null);
        }}
      />
    </div>
  );
};

export default AchievementsPage;

